import { albumHref } from '@/features/artists/artistHref'
import { PageHeader, pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import {
  CircleCheck,
  Disc3,
  Plus,
  ListMusic,
  Mic2,
  Search,
  SearchX,
  TriangleAlert,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import {
  useRequestTrack,
  useAlbumSearch,
  useArtistSearch,
  useBulkAddTracks,
  useJobs,
  useCatalogServices,
  useSearch,
} from "@/api/hooks";
import type {
  DownloadJob,
  CatalogServiceInfo,
  LibraryStatus,
  SearchResult,
} from "@/api/types";
import { usePendingPlayback } from "@/audio/pendingPlayback";
import { toQueueItem, usePlayer } from "@/audio/store";
import {
  AddToPlaylistSheet,
  SelectionBar,
  type SelectionAction,
  useSelection,
} from "@/components/selection";
import {
  Button,
  EmptyState,
  IconButton,
  Input,
  Spinner,
  toast,
  TrackListSkeleton,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { usePersistentState } from "@/lib/persistentState";

import {
  AddToPlaylistDialog,
  type AddToPlaylistTarget,
} from "./AddToPlaylistDialog";
import { AlbumGrid, AlbumGridSkeleton } from "./AlbumGrid";
import { ArtistGrid, ArtistGridSkeleton } from "./ArtistGrid";
import { summariseBulk } from "./bulkResult";
import { describeError } from "./describeError";
import {
  albumSourceNote,
  isSearchable,
  providerLabel,
  ProviderMenu,
  supportsAlbums,
  type ProviderFilterValue,
} from "./ProviderFilter";
import {
  SEARCH_MODES,
  SearchModeToggle,
  SEARCH_PANEL_ID,
  searchTabId,
  type SearchMode,
} from "./SearchModeToggle";
import {
  SearchResultRow,
  type ResultAction,
  type RowError,
} from "./SearchResultRow";
import { useDebouncedValue } from "./useDebouncedValue";

/** Search results have no id of their own; provider + external id is the identity. */
function keyOf(result: SearchResult): string {
  return `${result.provider}:${result.external_id}`;
}

/** What the add-track call told us, before the search query has caught up. */
interface AddedState {
  trackId: number;
  status: LibraryStatus;
}

function statusFor(
  result: SearchResult,
  added: AddedState | undefined,
  job: DownloadJob | undefined,
): LibraryStatus | undefined {
  // The server is authoritative once it says the file is on disk. Checking this
  // first matters because `added` is captured at click time and never advances:
  // without it the row stays on "Queued" forever after the download completes.
  if (result.library_status === "ready") return "ready";

  if (job) {
    if (job.status === "succeeded") return "ready";
    if (job.status === "running") return "downloading";
    if (job.status === "queued") return "pending";
    if (job.status === "failed") return "failed";
  }
  return added?.status ?? result.library_status ?? undefined;
}

export function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [input, setInput] = useState(() => searchParams.get("q") ?? "");
  const { debounced, flush } = useDebouncedValue(input, 300);
  const typed = input.trim();
  const query = debounced.trim();

  // Remembered across navigation, like the library's view toggle. The provider
  // filter deliberately is not: a sticky filter would silently hide a whole
  // provider's results long after the user forgot they set it.
  const [mode, setMode] = usePersistentState<SearchMode>(
    "search.mode",
    SEARCH_MODES,
    "songs",
  );
  const [provider, setProvider] = useState<ProviderFilterValue>("all");
  const [added, setAdded] = useState<Record<string, AddedState>>({});
  const [busyKeys, setBusyKeys] = useState<Record<string, boolean>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, RowError>>({});
  const [dismissedWarning, setDismissedWarning] = useState<string | null>(null);
  const [playlistTarget, setPlaylistTarget] =
    useState<AddToPlaylistTarget | null>(null);
  const [bulkPlaylistOpen, setBulkPlaylistOpen] = useState(false);
  // Selection mode can be on with nothing ticked yet, which useSelection cannot
  // represent on its own — it only turns `active` on once something is selected.
  const [selecting, setSelecting] = useState(false);

  const providersQuery = useCatalogServices();
  const providers = useMemo(
    () => providersQuery.data?.metadata ?? [{
      slug: 'library', name: 'Your library', health: 'available', message: '',
      can_search: true, can_search_artists: true, can_search_albums: true,
    } satisfies CatalogServiceInfo],
    [providersQuery.data],
  );
  const searchable = useMemo(() => providers.filter(isSearchable), [providers]);
  const albumProviders = useMemo(
    () => providers.filter(supportsAlbums),
    [providers],
  );

  // A provider that has since gone unhealthy must not silently filter every
  // result away, so the request falls back to searching everything.
  const activeProvider: ProviderFilterValue =
    provider !== "all" && !searchable.some((info) => info.slug === provider)
      ? "all"
      : provider;

  // Every library can browse its stored artists and albums.
  const albumsSupported = true;
  const albumsUsable =
    albumsSupported &&
    (activeProvider === "all" ||
      albumProviders.some((info) => info.slug === activeProvider));
  const albumsNote = albumSourceNote(albumProviders);
  const artistProviders = searchable.filter((info) => info.can_search_artists);
  const artistsSupported = true;
  const artistsUsable =
    artistsSupported &&
    (activeProvider === "all" ||
      artistProviders.some((info) => info.slug === activeProvider));
  const showingArtists = mode === "artists" && artistsSupported;
  const showingAlbums = mode === "albums" && albumsSupported;
  const artists = useArtistSearch(
    !showingArtists || !artistsUsable ? "" : query,
    activeProvider,
  );
  const artistResults = artists.data?.results ?? [];

  // Stored catalog search remains available when all acquisition is disabled.
  const search = useSearch(
    showingAlbums || showingArtists ? "" : query,
    activeProvider,
  );
  const results = useMemo(() => search.data?.results ?? [], [search.data]);

  const albums = useAlbumSearch(
    !showingAlbums || !albumsUsable ? "" : query,
    activeProvider,
  );
  const albumResults = albums.data?.results ?? [];

  const jobs = useJobs("active");
  const jobsByTrack = useMemo(() => {
    const map = new Map<number, DownloadJob>();
    for (const job of jobs.data?.results ?? []) map.set(job.track.id, job);
    return map;
  }, [jobs.data]);

  const playQueue = usePlayer((state) => state.playQueue);
  const enqueue = usePlayer((state) => state.enqueue);
  const playNextUp = usePlayer((state) => state.playNextUp);
  const addTrack = useRequestTrack();
  const bulkAdd = useBulkAddTracks();

  const selection = useSelection<string>();
  const clearSelection = selection.clear;
  const orderedKeys = useMemo(() => results.map(keyOf), [results]);
  const selectedRefs = useMemo(
    () =>
      results
        .filter((result) => selection.isSelected(keyOf(result)))
        .map((result) => ({
          provider: result.provider,
          external_id: result.external_id,
        })),
    [results, selection],
  );

  const selectionOn = selecting || selection.active;
  const canSelect = mode === "songs" && results.length > 0;
  const allSelected =
    orderedKeys.length > 0 &&
    orderedKeys.every((key) => selection.isSelected(key));

  function exitSelection(): void {
    setSelecting(false);
    clearSelection();
  }

  function startSelecting(key?: string): void {
    setSelecting(true);
    if (key) selection.select(key);
  }

  // A different query or provider means different rows; keeping ticks from the
  // old list would let the user act on something they can no longer see.
  useEffect(() => {
    setSelecting(false);
    clearSelection();
  }, [query, activeProvider, mode, clearSelection]);

  // Keep the address bar in step so a search survives a reload or a share.
  useEffect(() => {
    const current = searchParams.get("q") ?? "";
    if (current === debounced) return;
    const next = new URLSearchParams(searchParams);
    if (debounced) next.set("q", debounced);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }, [debounced, searchParams, setSearchParams]);

  const runAction = async (
    result: SearchResult,
    action: ResultAction,
  ): Promise<void> => {
    const key = keyOf(result);
    setBusyKeys((previous) => ({ ...previous, [key]: true }));
    setRowErrors((previous) => {
      if (!previous[key]) return previous;
      const { [key]: _removed, ...rest } = previous;
      return rest;
    });

    try {
      // The endpoint is idempotent: an already-downloaded track comes back with
      // job = null, so one call covers "play it" and "fetch it first".
      const response = await addTrack.mutateAsync({
        intent: action === "download" ? "save" : "play",
        provider: result.provider,
        external_id: result.external_id,
      });

      setAdded((previous) => ({
        ...previous,
        [key]: {
          trackId: response.library_item.track.id,
          status: response.library_item.status,
        },
      }));

      const item = toQueueItem(response.library_item);

      if (!item) {
        // Not on disk yet. Record what the user wanted so playback starts by
        // itself the moment the download lands — see @/audio/pendingPlayback.
        if (action !== "download") {
          usePendingPlayback
            .getState()
            .requestWhenReady(response.library_item.id, action, "search");
        }
        toast.info(
          `Downloading ${result.title}…`,
          action === "download"
            ? "Follow it from Downloads."
            : "It will start playing when it is ready.",
        );
        return;
      }

      switch (action) {
        case "play":
          playQueue([item], 0, { source: "search" });
          break;
        case "next":
          playNextUp([item]);
          toast.success("Playing next", result.title);
          break;
        case "queue":
          enqueue([item]);
          toast.success("Added to queue", result.title);
          break;
        case "download":
          toast.success("Added to library", result.title);
          break;
      }
    } catch (caught) {
      setRowErrors((previous) => ({
        ...previous,
        [key]: {
          message: describeError(caught, `Could not add ${result.title}.`),
          retry: () => void runAction(result, action),
        },
      }));
    } finally {
      setBusyKeys((previous) => ({ ...previous, [key]: false }));
    }
  };

  function downloadSelected(): void {
    if (selectedRefs.length === 0) return;
    bulkAdd.mutate(selectedRefs, {
      onSuccess: (result) => {
        toast.show(summariseBulk(result));
        // Anything short of a total failure is progress; leaving the selection
        // up would invite a second identical request.
        if (result.queued > 0 || result.already_present > 0) exitSelection();
      },
      onError: (caught) =>
        toast.error(
          "Could not add those songs",
          describeError(
            caught,
            "The request did not go through. Your selection is still here.",
          ),
        ),
    });
  }

  const selectionActions: SelectionAction[] = [
    {
      key: "download",
      label: "Add to library",
      icon: Plus,
      onSelect: downloadSelected,
      loading: bulkAdd.isPending,
    },
    {
      key: "playlist",
      label: "Add to playlist",
      icon: ListMusic,
      onSelect: () => setBulkPlaylistOpen(true),
    },
  ];

  const activeSearch = showingArtists ? artists : showingAlbums ? albums : search;
  const providerErrors = Object.entries(activeSearch.data?.provider_errors ?? {});
  const warningSignature = providerErrors
    .map(([slug, message]) => `${slug}:${message}`)
    .sort()
    .join("|");
  const showWarning =
    providerErrors.length > 0 && dismissedWarning !== warningSignature;

  const isSettling = typed !== query;
  const fetching = activeSearch.isFetching;
  const showSongSkeleton =
    typed.length >= 2 && !search.data && (search.isPending || isSettling);
  const showAlbumSkeleton =
    typed.length >= 2 && !albums.data && (albums.isPending || isSettling);

  const showArtistSkeleton =
    typed.length >= 2 && !artists.data && (artists.isPending || isSettling);
  const resultCount = activeSearch.data?.results.length ?? 0;
  const noun = showingArtists ? "artist" : showingAlbums ? "album" : "result";
  const liveMessage =
    typed.length < 2 ||
    (showingAlbums && !albumsUsable) ||
    (showingArtists && !artistsUsable)
      ? ""
      : fetching || isSettling
        ? "Searching"
        : activeSearch.isError && !activeSearch.data
          ? "Search failed"
          : `${resultCount} ${resultCount === 1 ? noun : `${noun}s`} for ${query}`;

  const showToolbarRow =
    albumsSupported || artistsSupported || providers.length > 1 || canSelect;

  return (
    <div className={pageClassName('browse')}>
      <PageHeader title="Search" />

      <div className="top-safe sticky z-20 -mx-4 mt-3 bg-surface-sunken px-4 py-2">
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            flush(input);
            // Submitting is the user saying they are done typing. Without an
            // explicit blur the keyboard stays up over the results they just
            // asked for — iOS does not dismiss it for a form that never
            // navigates.
            (event.currentTarget.querySelector("input") as HTMLInputElement | null)?.blur();
          }}
        >
          <Input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            aria-label="Search for music"
            placeholder="Songs, artists, albums"
            // Not type="search": Chrome adds its own clear button, which would
            // sit on top of the one in the trailing slot.
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            spellCheck={false}
            leading={<Search className="h-5 w-5" />}
            trailing={
              <span className="flex items-center">
                {fetching ? (
                  <Spinner
                    size="sm"
                    className="mr-2 text-text-subtle"
                    label="Searching"
                  />
                ) : null}
                {input ? (
                  <IconButton
                    aria-label="Clear search"
                    size="sm"
                    onClick={() => {
                      setInput("");
                      flush("");
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </IconButton>
                ) : null}
              </span>
            }
          />
        </form>

        {/* Tabs can scroll on small screens; source and selection stay compact. */}
        {showToolbarRow ? (
          <div className="mt-2 flex items-center gap-1.5 pb-1">
            {albumsSupported || artistsSupported ? (
              <SearchModeToggle
                value={mode}
                onChange={setMode}
                albumsDisabled={!albumsUsable && mode !== "albums"}
                albumsReason={albumsNote}
                artistsDisabled={!artistsUsable && mode !== "artists"}
                artistsReason="Choose a source with artist search."
              />
            ) : null}

            <div className="min-w-0 flex-1" />

            {providers.length > 1 ? (
              <ProviderMenu
                providers={providers}
                value={activeProvider}
                onChange={setProvider}
                className="min-w-0"
              />
            ) : null}

            {canSelect ? (
              selectionOn ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 shrink-0 rounded-full px-2 text-xs"
                  disabled={allSelected}
                  onClick={() => selection.selectAll(orderedKeys)}
                >
                  Select all
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 shrink-0 rounded-full px-2 text-xs"
                  aria-label="Select results"
                  onClick={() => startSelecting()}
                >
                  <CircleCheck className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Select</span>
                </Button>
              )
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="pt-2" id={SEARCH_PANEL_ID} role={albumsSupported || artistsSupported ? "tabpanel" : undefined} aria-labelledby={albumsSupported || artistsSupported ? searchTabId(mode) : undefined}>
        {/* Mounted unconditionally: a live region only announces changes that
            happen while it is already on the page. */}
        <p role="status" className="sr-only">
          {liveMessage}
        </p>

        {showWarning ? (
          <div
            role="status"
            className="mb-3 flex items-start gap-3 rounded-card border border-border bg-surface-raised p-3"
          >
            <TriangleAlert
              className="mt-0.5 h-5 w-5 shrink-0 text-warning"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-text">
                Some metadata services did not answer
              </p>
              {providerErrors.map(([slug, message]) => (
                <p key={slug} className="mt-0.5 text-sm text-text-muted">
                  <span className="text-text">
                    {providerLabel(providers, slug)}:
                  </span>{" "}
                  {message}
                </p>
              ))}
            </div>
            <IconButton
              aria-label="Dismiss metadata service warning"
              size="sm"
              onClick={() => setDismissedWarning(warningSignature)}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
        ) : null}

        {showingArtists && !artistsUsable ? (
          <EmptyState
            icon={Mic2}
            title="No artist search from this music source"
            action={{ label: "Search all music sources", onClick: () => setProvider("all") }}
          />
        ) : showingAlbums && !albumsUsable ? (
          <EmptyState
            icon={Disc3}
            title="No albums from this music source"
            description={`${albumsNote} Search everything, or switch back to songs.`}
            action={{
              label: "Search all music sources",
              onClick: () => setProvider("all"),
            }}
          >
            <Button variant="ghost" size="sm" onClick={() => setMode("songs")}>
              Show songs instead
            </Button>
          </EmptyState>
        ) : typed.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Find something to play"
            description={
              showingArtists
                ? "Find an artist and explore their songs and albums."
                : showingAlbums
                  ? "Search for an album, then take the whole thing or just the songs you want."
                  : "Search your music sources, then play a song or add it to your library."
            }
          />
        ) : typed.length < 2 ? (
          <EmptyState
            icon={Search}
            title="Keep typing"
            description="Enter at least two characters to search."
          />
        ) : showingArtists ? (
          showArtistSkeleton ? (
            <ArtistGridSkeleton />
          ) : artists.isError && !artists.data ? (
            <ErrorNotice title="That search did not go through" busy={artists.isFetching} onRetry={() => void artists.refetch()}>
              {describeError(artists.error, "The artist search failed.")}
            </ErrorNotice>
          ) : artistResults.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title={`No artists for “${query}”`}
              description="Try the artist’s name without a song title."
            />
          ) : (
            <ArtistGrid artists={artistResults} />
          )
        ) : showingAlbums ? (
          showAlbumSkeleton ? (
            <AlbumGridSkeleton count={8} />
          ) : albums.isError && !albums.data ? (
            <ErrorNotice title="That search did not go through" busy={albums.isFetching} onRetry={() => void albums.refetch()}>
              {describeError(albums.error, "The album search failed.")}
            </ErrorNotice>
          ) : albumResults.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title={`No albums for “${query}”`}
              description="Check the spelling, or search songs instead."
              action={{
                label: "Show songs instead",
                onClick: () => setMode("songs"),
              }}
            />
          ) : (
            <AlbumGrid
              albums={albumResults}
              providers={providers}
              showProvider={
                activeProvider === "all" && albumProviders.length > 1
              }
              onSelect={(album) => void navigate(albumHref(album))}
              className={cn(
                "transition-opacity",
                albums.isFetching && "opacity-60",
              )}
            />
          )
        ) : showSongSkeleton ? (
          <TrackListSkeleton count={8} />
        ) : search.isError && !search.data ? (
          <ErrorNotice title="That search did not go through" busy={search.isFetching} onRetry={() => void search.refetch()}>
            {describeError(search.error, "The search request failed.")}
          </ErrorNotice>
        ) : results.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title={`No results for “${query}”`}
            description="Check the spelling, or try a shorter search."
            action={
              activeProvider === "all"
                ? undefined
                : {
                    label: "Search all music sources",
                    onClick: () => setProvider("all"),
                  }
            }
          />
        ) : (
          <>
            <ul
              className={cn(
                "transition-opacity",
                search.isFetching && "opacity-60",
              )}
            >
              {results.map((result) => {
                const key = keyOf(result);
                const local = added[key];
                const trackId = local?.trackId ?? result.track_id;
                const job =
                  trackId === null ? undefined : jobsByTrack.get(trackId);
                const status = statusFor(result, local, job);

                return (
                  <SearchResultRow
                    key={key}
                    result={result}
                    status={status}
                    progress={
                      job?.status === "running" ? job.progress : undefined
                    }
                    busy={busyKeys[key] === true}
                    error={rowErrors[key]}
                    showProvider
                    providerName={providerLabel(providers, result.provider)}
                    selectable={selectionOn}
                    selected={selection.isSelected(key)}
                    onSelectChange={(shiftKey) =>
                      shiftKey
                        ? selection.toggleRange(key, orderedKeys)
                        : selection.toggle(key)
                    }
                    onStartSelecting={() => startSelecting(key)}
                    onAction={(action) => void runAction(result, action)}
                    onAddToPlaylist={() =>
                      setPlaylistTarget({
                        title: result.title,
                        provider: result.provider,
                        externalId: result.external_id,
                        trackId,
                      })
                    }
                    onDismissError={() =>
                      setRowErrors((previous) => {
                        const { [key]: _removed, ...rest } = previous;
                        return rest;
                      })
                    }
                  />
                );
              })}
            </ul>

            {/* The selection bar is docked above the player, so the last row
                needs room to clear it. */}
            {selectionOn ? <div aria-hidden="true" className="h-16" /> : null}
          </>
        )}
      </div>

      {playlistTarget ? (
        <AddToPlaylistDialog
          key={`${playlistTarget.provider}:${playlistTarget.externalId}`}
          target={playlistTarget}
          onOpenChange={(open) => {
            if (!open) setPlaylistTarget(null);
          }}
        />
      ) : null}

      {/* Mounted only while it is open, so it does not fetch playlists for a
          user who never opens it. */}
      {bulkPlaylistOpen ? (
        <AddToPlaylistSheet
          open
          onOpenChange={setBulkPlaylistOpen}
          providerRefs={selectedRefs}
          onAdded={() => exitSelection()}
        />
      ) : null}

      {selectionOn && mode === "songs" ? (
        <SelectionBar
          count={selection.count}
          onClear={exitSelection}
          actions={selectionActions}
        />
      ) : null}
    </div>
  );
}
